import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import { useAccount } from "../context/AccountContext";
import { GoogleMap, Marker, useLoadScript, InfoWindow, Circle } from "@react-google-maps/api";
import "./PartialDispatchMap.css";

const PartialDispatchMap = () => {
  const { currentAccount } = useAccount();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!selectedOrder) return;

    const updated = orders.find(o => o.id === selectedOrder.id);
    if (updated) {
      setSelectedOrder(updated); // 🔥 refresh selected order with new data
    }
  }, [orders]);

  useEffect(() => {
    if (!currentAccount?.uid) return;

    const db = getDatabase();
    const ordersRef = ref(db, "transport_orders");

    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setOrders([]);
        return;
      }

      const list = [];

      Object.entries(data).forEach(([transporterId, transporterOrders]) => {
        Object.entries(transporterOrders || {}).forEach(([id, order]) => {
          const isMyOrder =
            order.clientUid === currentAccount.uid ||
            order.clientId === currentAccount.uid ||
            order.companyName === currentAccount.company_name;

          const isPartial = (order.status || "").toLowerCase() === "partial dispatched";

          if (isMyOrder && isPartial) {
            list.push({
              id,
              transporterId,
              ...order,
            });
          }
        });
      });

      const sortedList = list.sort((a, b) => {
        const aLive = a?.dispatch?.items?.some(
          (i) => i?.location?.sharing === true
        );
        const bLive = b?.dispatch?.items?.some(
          (i) => i?.location?.sharing === true
        );

        // 🔥 LIVE orders first
        if (aLive && !bLive) return -1;
        if (!aLive && bLive) return 1;

        return 0;
      });

      setOrders(sortedList);
    });

    return () => unsubscribe();
  }, [currentAccount?.uid, currentAccount?.company_name]);


  return (
    <div className="pd-container">
      {/* LEFT SIDE - Orders List */}
      <div className="track-pd-left">
        <h2>Dispatched Orders</h2>

        {orders.length === 0 ? (
          <p>No partial dispatch orders</p>
        ) : (
          orders.map((order) => {
            const isLive = order?.dispatch?.items?.some(
              (i) => i?.location?.sharing === true
            );

            return (
              <div
                key={order.id}
                className={`track-pd-card ${selectedOrder?.id === order.id ? "active" : ""} ${isLive ? "live" : "offline"
                  }`}
                onClick={() => setSelectedOrder(order)}
              >
                <div className="track-card-header">
                  <h5>{order.orderId}</h5>
                  <span className={`badge ${isLive ? "on" : "off"}`}>
                    {isLive ? "LIVE" : "OFF"}
                  </span>
                </div>
                <p>{order.from} → {order.to}</p>
                <p>{order.quantity} {order.quantityUnit}</p>
                {/* <p className="status-text">Status: {order.status}</p> */}
              </div>
            );
          })
        )}
      </div>

      {/* RIGHT SIDE - Live Tracking */}
      <div className="pd-right">
        {selectedOrder ? (
         <LiveTracking key={selectedOrder.id} order={selectedOrder} />
        ) : (
          <div className="pd-placeholder">
            Select an order to view live tracking
          </div>
        )}
      </div>
    </div>
  );
};

function LiveTracking({ order }) {
  const [address, setAddress] = React.useState("");
  const [pulseScale, setPulseScale] = useState(1);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  const mapRef = React.useRef(null);
  const timeoutRef = React.useRef(null);
  const growingRef = useRef(true);

  const items = order?.dispatch?.items || [];

  const liveItem = useMemo(() => {
    return items.find(i => i?.location?.sharing === true) ||
           items.find(i => i?.location);
  }, [items]);

  const location = liveItem?.location;
  const isLive = !!location?.sharing;

  const isMapReady = isLoaded && typeof window !== "undefined" && !!window.google;

  // Pulse animation for glow
  useEffect(() => {
    if (!isLive) {
      setPulseScale(1);
      return;
    }

    const interval = setInterval(() => {
      setPulseScale((prev) => {
        if (prev >= 1.35) growingRef.current = false;
        if (prev <= 1) growingRef.current = true;
        return growingRef.current ? prev + 0.035 : prev - 0.035;
      });
    }, 120);

    return () => clearInterval(interval);
  }, [isLive]);

  // Pan to location
  React.useEffect(() => {
    if (!isMapReady || !mapRef.current || !location?.lat || !location?.lng) return;
    mapRef.current.panTo({ lat: location.lat, lng: location.lng });
    mapRef.current.setZoom(16);
  }, [location?.lat, location?.lng, isMapReady]);

  // Geocoding
  useEffect(() => {
    if (!isMapReady || !location?.lat || !location?.lng) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: location.lat, lng: location.lng } },
        (results, status) => {
          if (status === "OK" && results?.[0]) {
            setAddress(results[0].formatted_address);
          }
        }
      );
    }, 1200);
  }, [location?.lat, location?.lng, isMapReady]);

  // ✅ Truck Icon Memo (NOW BEFORE early return)
  const truckIcon = React.useMemo(() => {
    if (!window.google) return null;

    const color = isLive ? "#1E88E5" : "#9E9E9E";

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <!-- Glow Rings -->
        ${isLive ? `
          <circle cx="12" cy="12" r="${8 * pulseScale}" fill="none" stroke="#1E88E5" stroke-width="1.8" opacity="0.35"/>
          <circle cx="12" cy="12" r="${11.5 * pulseScale}" fill="none" stroke="#1E88E5" stroke-width="1.2" opacity="0.18"/>
        ` : ''}

        <!-- Main Truck -->
        <rect x="1" y="3" width="15" height="13" rx="1" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <circle cx="5.5" cy="18.5" r="2.5" stroke="${color}" stroke-width="2" fill="#fff"/>
        <circle cx="18.5" cy="18.5" r="2.5" stroke="${color}" stroke-width="2" fill="#fff"/>
       
        <!-- Center highlight -->
        ${isLive ? `<circle cx="12" cy="12" r="1.8" fill="#1E88E5"/>` : ''}
      </svg>`;

    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(16, 16),
    };
  }, [isLive, pulseScale]);

  // Early return - AFTER ALL HOOKS
  if (!isMapReady || !location?.lat || !location?.lng) {
    return (
      <p className="loading-map">
        {isMapReady ? "Waiting for GPS..." : "Loading live map..."}
      </p>
    );
  }

  return (
    <div className="tracking-container">
      <div className="tracking-header">
        <h3>Live Tracking - {order.orderId}</h3>
        <span className={`status-badge ${isLive ? "on" : "off"}`}>
          {isLive ? "● LIVE" : "LOCATION OFF"}
        </span>
      </div>

      <div className="live-address text-primary mb-4">
        📍 {address || "Fetching address..."}
      </div>

      <div className="info-grid">
        {/* <div className="info-card">
          <span className="label">👨‍✈️ Driver: </span>
          <span className="value">{liveItem?.driverName || "N/A"}</span>
        </div>
        <div className="info-card">
          <span className="label">📞 Phone: </span>
          <span className="value">{liveItem?.driverNumber || "N/A"}</span>
        </div> */}
        <div className="info-card">
          <span className="label">🚀 Speed: </span>
          <span className="value">
            {location?.speed ? location.speed.toFixed(1) : 0} km/h
          </span>
        </div>
        <div className="info-card">
          <span className="label">📍 Latitude: </span>
          <span className="value">{location?.lat || 0}</span>
        </div>
        <div className="info-card">
          <span className="label">📍 Longitude: </span>
          <span className="value">{location?.lng || 0}</span>
        </div>
        <div className="info-card">
          <span className="label">🎯 Accuracy: </span>
          <span className="value">{location?.accuracy || 0} m</span>
        </div>
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 2,
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "12px",
            background: "white",
            padding: "10px 14px",
            borderRadius: "12px",
            boxShadow: "0 2px 12px rgba(0,0,0,.12)",
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>
              {liveItem?.driverName || "Driver"}
            </div>
            <div style={{ fontSize: "13px", color: "#666" }}>
              {liveItem?.driverNumber || "N/A"}
            </div>
          </div>

          <div
            style={{
              width: "1px",
              height: "32px",
              background: "#e5e7eb",
            }}
          />

          <div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              Truck Number
            </div>
            <div style={{ fontWeight: 600 }}>
              {liveItem?.vehicleNumber ||
                liveItem?.truckNumber ||
                liveItem?.truckNo ||
                "N/A"}
            </div>
          </div>
        </div>
      </div>

      <GoogleMap
        onLoad={(map) => (mapRef.current = map)}
        zoom={15}
        center={{ lat: location.lat, lng: location.lng }}
        mapContainerStyle={{
          width: "100%",
          height: "400px",
          borderRadius: "12px",
        }}
        key={`map-${order.id}`}
      >
        {window.google && truckIcon && (
          <Marker
            key={`${location.lat}-${location.lng}-${isLive}`}
            position={{ lat: location.lat, lng: location.lng }}
            icon={truckIcon}
          />
        )}
      </GoogleMap>
    </div>
  );
}
export default PartialDispatchMap;